import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Autocomplete, TextField } from "@mui/material";
import { professionList } from "../../json/professionList";
import { qualificationList } from "../../json/qualificationList";
import { dietList } from "../../json/dietList";
import { communityList } from "../../json/communityList";
import { heightList } from "../../json/heightList";
import { maritalStatusList } from "../../json/maritalStatusList";
import { motherTongueList } from "../../json/motherTongueList";
import { religionList } from "../../json/religionList";
import { stateCityList } from "../../json/stateCityList";
import { workingInList } from "../../json/workingInList";
import { server } from "../../server";
import Header from "./Header";
import Right from "./Right";
import { useDispatch, useSelector } from "react-redux";
import { fetchMyProfile } from "../../redux/myProfileSlice";

/* ---------- helpers ---------- */
const formatDOBForInput = (dob) => {
  if (!dob) return "";

  if (typeof dob === "number") {
    return new Date(dob).toISOString().split("T")[0];
  }

  if (typeof dob === "string") {
    return dob.includes("T") ? dob.split("T")[0] : dob;
  }

  if (dob instanceof Date) {
    return dob.toISOString().split("T")[0];
  }

  return "";
};

const MuiAutocomplete = ({ label, options, value, onChange, disabled }) => (
  <Autocomplete
    options={options}
    getOptionLabel={(option) => option.Name}
    value={options.find((o) => o.Name === value) || null}
    onChange={(e, newValue) => onChange(newValue ? newValue.Name : "")}
    disabled={disabled}
    renderInput={(params) => (
      <TextField {...params} label={label} size="small" />
    )}
  />
);

const MuiTextInput = ({ label, value, onChange, type = "text" }) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    type={type}
    value={value}
    onChange={onChange}
  />
);

export default function EditProfile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.myProfile);

  useEffect(() => {
    dispatch(fetchMyProfile());
  }, [dispatch]);

  const [form, setForm] = useState({
    name: "",
    dob: "",
    height: "",
    email: "",
    mobile: "",
    religion: "",
    motherTongue: "",
    community: "",
    maritalStatus: "",
    diet: "",
    state: "",
    city: "",
    qualification: "",
    workingIn: "",
    profession: "",
    annualIncome: "",
    familyType: "",
    fatherStatus: "",
    motherStatus: "",
    images: [],
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getFilteredCommunityOptions = (religion) => {
    if (!religion) return [];

    return communityList
      .map((i) => i.Name)
      .filter((c) => c.startsWith(religion + "-")) // ✅ FIXED
      .map((c) => ({
        Name: c.replace(religion + "-", ""), // Rajput
        raw: c, // Hindu-Rajput (saved value)
      }));
  };

  const filteredCommunityOptions = getFilteredCommunityOptions(form.religion);

  const update = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  /* ---------- populate form ---------- */
  useEffect(() => {
    if (!profile) return;

    setForm({
      name: profile.name || "",
      dob: formatDOBForInput(profile.profile?.dob),
      height: profile.profile?.height || "",
      email: profile.email || "",
      mobile: profile.mobile || "",
      religion: profile.profile?.religion || "",
      motherTongue: profile.profile?.motherTongue || "",
      community: profile.profile?.community || "",
      maritalStatus: profile.profile?.maritalStatus || "",
      diet: profile.profile?.diet || "",
      state: profile.profile?.state || "",
      city: profile.profile?.city || "",
      qualification: profile.profile?.qualification || "",
      workingIn: profile.profile?.workingIn || "",
      profession: profile.profile?.profession || "",
      annualIncome: profile.profile?.annualIncome || "",
      familyType: profile.profile?.familyType || "",
      fatherStatus: profile.profile?.fatherStatus || "",
      motherStatus: profile.profile?.motherStatus || "",
      images: profile.images || [],
    });

    setLoading(false);
  }, [profile]);

  /* ---------- save ---------- */
  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("authToken");

      const dobTimestamp = form.dob
        ? new Date(form.dob + "T00:00:00Z").getTime()
        : null;

      const payload = {
        mobile: form.mobile,
        profile: {
          name: form.name,
          dob: dobTimestamp,
          height: form.height,
          religion: form.religion,
          motherTongue: form.motherTongue,
          community: form.community,
          maritalStatus: form.maritalStatus,
          diet: form.diet,
          state: form.state,
          city: form.city,
          qualification: form.qualification,
          workingIn: form.workingIn,
          profession: form.profession,
          annualIncome: form.annualIncome,
          familyType: form.familyType,
          fatherStatus: form.fatherStatus,
          motherStatus: form.motherStatus,
          images: form.images,
        },
      };

      const resp = await fetch(`${server}/profile/EditProfile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) throw new Error("Failed to update profile");

      alert("Profile updated successfully!");
      navigate(-1);
    } catch (err) {
      alert(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const stateOptions = Array.from(
    new Map(stateCityList.map((i) => [i.State, { Name: i.State }])).values()
  );

  const cityOptions = form.state
    ? stateCityList
        .filter((i) => i.State === form.state)
        .map((i) => ({ Name: i.City }))
    : [];

  const normalize = (str = "") =>
    str.toLowerCase().replace(/\s+/g, "").replace(/\/+/g, "");

  return (
    <>
      <Header />
      <section>
        <div className="container pt-3">
          <div className="row g-3">
            <div className="col-lg-8 col-12 mb-5">
              <div className="d-flex align-items-center mb-3">
                <Link
                  onClick={() => navigate(-1)}
                  className="icon-shape me-3 icon-lg bg-light-red rounded-circle"
                >
                  <i className="bi bi-chevron-left fs-3 text-primary"></i>
                </Link>
                <div>
                  <h3 className="fw-medium ls-sm text-dark lh-1 fs-4 m-0">
                    Edit / Update Profile
                  </h3>
                  <p className="lh-sm fw-normal m-0">
                    Update your profile basic details
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-4 p-5">
                <div className="row g-3">
                  <div className="col-lg-6 col-12">
                    <MuiAutocomplete
                      label="Height"
                      options={heightList}
                      value={form.height}
                      onChange={(v) => update("height", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiAutocomplete
                      label="Marital Status"
                      options={maritalStatusList}
                      value={form.maritalStatus}
                      onChange={(v) => update("maritalStatus", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiAutocomplete
                      label="Diet"
                      options={dietList}
                      value={form.diet}
                      onChange={(v) => update("diet", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiAutocomplete
                      label="Religion"
                      options={religionList}
                      value={form.religion}
                      onChange={(v) => {
                        update("religion", v);
                        update("community", "");
                      }}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiAutocomplete
                      label="Mother Tongue"
                      options={motherTongueList}
                      value={form.motherTongue}
                      onChange={(v) => update("motherTongue", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <Autocomplete
                      options={filteredCommunityOptions}
                      getOptionLabel={(option) => option.Name}
                      value={
                        filteredCommunityOptions.find(
                          (o) =>
                            normalize(o.raw) === normalize(form.community) ||
                            normalize(o.Name) === normalize(form.community)
                        ) || null
                      }
                      onChange={(e, newValue) =>
                        update("community", newValue ? newValue.raw : "")
                      }
                      disabled={!form.religion}
                      renderInput={(params) => (
                        <TextField {...params} label="Community" size="small" />
                      )}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiAutocomplete
                      label="Qualification"
                      options={qualificationList}
                      value={form.qualification}
                      onChange={(v) => update("qualification", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiAutocomplete
                      label="Working In"
                      options={workingInList}
                      value={form.workingIn}
                      onChange={(v) => update("workingIn", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiAutocomplete
                      label="Profession"
                      options={professionList}
                      value={form.profession}
                      onChange={(v) => update("profession", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiTextInput
                      label="Annual Income"
                      value={form.annualIncome}
                      onChange={(e) => update("annualIncome", e.target.value)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiTextInput
                      label="Family Type"
                      value={form.familyType}
                      onChange={(e) => update("familyType", e.target.value)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiTextInput
                      label="Father Status"
                      value={form.fatherStatus}
                      onChange={(e) => update("fatherStatus", e.target.value)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiTextInput
                      label="Mother Status"
                      value={form.motherStatus}
                      onChange={(e) => update("motherStatus", e.target.value)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiAutocomplete
                      label="State"
                      options={stateOptions}
                      value={form.state}
                      onChange={(v) => update("state", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MuiAutocomplete
                      label="City"
                      options={cityOptions}
                      value={form.city}
                      onChange={(v) => update("city", v)}
                      disabled={!form.state}
                    />
                  </div>
                </div>

                <div>
                  <span
                    className="btn btn-danger rounded-pill fw-normal px-6 mt-4 mb-2"
                    onClick={handleSaveChanges}
                    disabled={saving}
                  >
                    {saving ? "Saving Changes" : "Update Details"}
                  </span>
                </div>
              </div>
            </div>
            <Right />
          </div>
        </div>
      </section>
    </>
  );
}
